import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { ChevronLeft, Users, UserPlus, Trash2, ShieldAlert, Search, Ban, RotateCcw, Upload, Clock, Scissors, BookOpen, Film } from "lucide-react";
import * as XLSX from "xlsx";
import { Textarea } from "@/components/ui/textarea";
import {
  useAdminChallengeQuery,
  useAdminAddChallengeParticipantMutation,
  useAdminRemoveChallengeParticipantMutation,
  useAdminDisqualifyParticipantMutation,
  useAdminResetChallengeParticipantUsageMutation,
  useAdminResetAllChallengeParticipantsUsageMutation,
  AdminChallengeQuery,
} from "@/generated/graphql";

// The challenge detail query selects a narrower subset of participant fields
// than the full AdminChallengeParticipant type, so state must match what's
// actually returned.
type AdminChallengeParticipant = NonNullable<NonNullable<NonNullable<AdminChallengeQuery['adminChallenge']>['participants']>[number]>;
import { formatDateTime } from "@/lib/format";
import { formatHoursMinutes } from "@/lib/video-metadata";
import { useToast } from "@/hooks/use-toast";

const AdminChallengeParticipants = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { challengeId } = useParams<{ challengeId: string }>();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isDisqualifyModalOpen, setIsDisqualifyModalOpen] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<AdminChallengeParticipant | null>(null);
  const [participantEmail, setParticipantEmail] = useState("");
  const [disqualificationReason, setDisqualificationReason] = useState("");
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importResults, setImportResults] = useState<{ added: string[]; created: string[]; skipped: string[]; } | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isResetUsageModalOpen, setIsResetUsageModalOpen] = useState(false);
  const [isResetAllUsageModalOpen, setIsResetAllUsageModalOpen] = useState(false);

  const { data, loading, error, refetch } = useAdminChallengeQuery({
    variables: { id: challengeId! },
    skip: !challengeId,
    fetchPolicy: "cache-and-network",
  });

  const [addParticipant, { loading: addingParticipant }] = useAdminAddChallengeParticipantMutation();
  const [removeParticipant, { loading: removingParticipant }] = useAdminRemoveChallengeParticipantMutation();
  const [disqualifyParticipant, { loading: disqualifying }] = useAdminDisqualifyParticipantMutation();
  const [resetParticipantUsage, { loading: resettingUsage }] = useAdminResetChallengeParticipantUsageMutation();
  const [resetAllUsage, { loading: resettingAllUsage }] = useAdminResetAllChallengeParticipantsUsageMutation();

  const challenge = data?.adminChallenge;
  const allParticipants = challenge?.participants || [];
  const participants = search
    ? allParticipants.filter(
        (p) =>
          p.user_name?.toLowerCase().includes(search.toLowerCase()) ||
          p.user_email?.toLowerCase().includes(search.toLowerCase())
      )
    : allParticipants;

  const handleAddParticipant = async () => {
    if (!challengeId || !participantEmail) return;
    try {
      await addParticipant({ variables: { challengeId, email: participantEmail } });
      toast({ title: t("admin.challenges.toast.participantAdded") });
      setIsAddModalOpen(false);
      setParticipantEmail("");
      refetch();
    } catch (err: any) {
      toast({ title: err.message || t("admin.challenges.toast.participantAddError"), variant: "destructive" });
    }
  };

  const handleRemoveParticipant = async () => {
    if (!challengeId || !selectedParticipant) return;
    try {
      await removeParticipant({ variables: { challengeId, participantId: selectedParticipant.id } });
      toast({ title: t("admin.challenges.toast.participantRemoved") });
      setIsRemoveModalOpen(false);
      setSelectedParticipant(null);
      refetch();
    } catch (err: any) {
      toast({ title: err.message || t("admin.challenges.toast.participantRemoveError"), variant: "destructive" });
    }
  };

  const handleDisqualify = async () => {
    if (!selectedParticipant) return;
    const isCurrentlyDisqualified = selectedParticipant.disqualified;
    try {
      await disqualifyParticipant({
        variables: {
          participantId: selectedParticipant.id,
          disqualified: !isCurrentlyDisqualified,
          reason: !isCurrentlyDisqualified ? disqualificationReason : null,
        },
      });
      toast({
        title: isCurrentlyDisqualified
          ? t("admin.challenges.toast.participantRequalified")
          : t("admin.challenges.toast.participantDisqualified"),
      });
      setIsDisqualifyModalOpen(false);
      setSelectedParticipant(null);
      setDisqualificationReason("");
      refetch();
    } catch (err: any) {
      toast({ title: err.message || t("admin.challenges.toast.disqualifyError"), variant: "destructive" });
    }
  };

  const handleResetUsage = async () => {
    if (!challengeId || !selectedParticipant) return;
    try {
      await resetParticipantUsage({ variables: { challengeId, participantId: selectedParticipant.id } });
      toast({ title: t("admin.challenges.usageReset") });
      setIsResetUsageModalOpen(false);
      setSelectedParticipant(null);
      refetch();
    } catch (err: any) {
      toast({ title: err.message || t("admin.challenges.usageResetError"), variant: "destructive" });
    }
  };

  const handleResetAllUsage = async () => {
    if (!challengeId) return;
    try {
      await resetAllUsage({ variables: { challengeId } });
      toast({ title: t("admin.challenges.allUsageReset") });
      setIsResetAllUsageModalOpen(false);
      refetch();
    } catch (err: any) {
      toast({ title: err.message || t("admin.challenges.usageResetError"), variant: "destructive" });
    }
  };

  const extractParticipantsFromFile = (file: File): Promise<{ email: string; phone?: string; name?: string }[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[sheetName]);

          if (!rows.length) {
            reject(new Error(t("admin.challenges.importNoEmails")));
            return;
          }

          const headers = Object.keys(rows[0]);
          const emailKey = headers.find((h) => /e-?mail/i.test(h)) || headers.find((h) => h.toLowerCase() === "email") || headers[0];
          const phoneKey = headers.find((h) => /whatsapp|phone|telefone|celular/i.test(h));
          const nameKey = headers.find((h) => /^nome$/i.test(h)) || headers.find((h) => /^name$/i.test(h));

          const seen = new Set<string>();
          const participants: { email: string; phone?: string; name?: string }[] = [];

          for (const row of rows) {
            const email = String(row[emailKey] || "").trim().toLowerCase();
            if (!email || !email.includes("@") || seen.has(email)) continue;
            seen.add(email);

            const phone = phoneKey ? String(row[phoneKey] || "").trim() : undefined;
            const name = nameKey ? String(row[nameKey] || "").trim() : undefined;
            participants.push({ email, phone: phone || undefined, name: name || undefined });
          }

          if (!participants.length) {
            reject(new Error(t("admin.challenges.importNoEmails")));
            return;
          }
          resolve(participants);
        } catch {
          reject(new Error(t("admin.challenges.importNoEmails")));
        }
      };
      reader.onerror = () => reject(new Error(t("admin.challenges.importNoEmails")));
      reader.readAsArrayBuffer(file);
    });
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !challengeId) return;

    // Reset the input so the same file can be re-selected
    e.target.value = "";

    setIsImporting(true);
    setImportResults(null);

    try {
      const participants = await extractParticipantsFromFile(file);
      const results = { added: [] as string[], created: [] as string[], skipped: [] as string[] };

      for (const { email, phone, name } of participants) {
        try {
          const { data: result } = await addParticipant({ variables: { challengeId, email, phone, name } });
          const wasCreated = result?.adminAddChallengeParticipant?.was_created;
          if (wasCreated) {
            results.created.push(email);
          } else {
            results.added.push(email);
          }
        } catch {
          // Already a participant or other non-fatal error
          results.skipped.push(email);
        }
      }

      setImportResults(results);
      refetch();
    } catch (err: any) {
      toast({ title: err.message, variant: "destructive" });
      setIsImportModalOpen(false);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate("/admin/challenges")}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                {t("common.back")}
              </Button>
              <div className="flex items-center gap-2">
                <Users className="w-6 h-6 text-primary" />
                <div>
                  <h1 className="text-xl font-bold">
                    {t("admin.challenges.participants")}
                    {challenge ? ` — ${challenge.name}` : ""}
                  </h1>
                  <p className="text-sm text-muted-foreground">{allParticipants.length} {t("admin.challenges.participantCount").toLowerCase()}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-red-500 border-red-500">ADMIN</Badge>
              <Button variant="outline" onClick={() => setIsResetAllUsageModalOpen(true)}>
                <RotateCcw className="w-4 h-4 mr-2" />
                {t("admin.challenges.resetAllUsage")}
              </Button>
              <Button variant="outline" onClick={() => { setImportResults(null); setIsImportModalOpen(true); }}>
                <Upload className="w-4 h-4 mr-2" />
                {t("admin.challenges.importParticipants")}
              </Button>
              <Button onClick={() => setIsAddModalOpen(true)}>
                <UserPlus className="w-4 h-4 mr-2" />
                {t("admin.challenges.addParticipant")}
              </Button>
            </div>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t("admin.challenges.participants")}</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder={t("admin.challenges.searchPlaceholder")}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-8 max-w-xs"
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading && !data ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-500">{t("admin.common.error")}</div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table className="table-fixed w-[1400px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[140px]">{t("common.name")}</TableHead>
                        <TableHead className="w-[180px]">{t("common.email")}</TableHead>
                        <TableHead className="w-[90px]">{t("admin.challenges.totalViews")}</TableHead>
                        <TableHead className="w-[60px]">{t("admin.challenges.clipCount")}</TableHead>
                        <TableHead className="w-[90px]">{t("admin.challenges.status")}</TableHead>
                        <TableHead className="w-[480px]">{t("admin.users.usage")}</TableHead>
                        <TableHead className="w-[120px]">{t("admin.challenges.createdAt")}</TableHead>
                        <TableHead className="w-[160px]">{t("admin.challenges.actions")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {participants.map((p) => {
                        const sub = p.subscription;
                        const timeUsed = sub?.current_month_processing_seconds ?? 0;
                        const timeLimit = sub?.plan?.processing_seconds ?? 0;
                        const timePercent = timeLimit > 0 ? Math.min((timeUsed / timeLimit) * 100, 100) : 0;

                        const clipsUsed = sub?.current_month_clips ?? 0;
                        const clipsLimit = sub?.plan?.max_clips ?? 0;
                        const clipsPercent = clipsLimit > 0 && clipsLimit < 1000000 ? Math.min((clipsUsed / clipsLimit) * 100, 100) : 0;

                        const libTimeUsed = sub?.current_month_library_video_seconds ?? 0;
                        const libTimeLimit = sub?.plan?.max_library_video_seconds ?? 0;
                        const libTimePercent = libTimeLimit > 0 ? Math.min((libTimeUsed / libTimeLimit) * 100, 100) : 0;

                        const libClipsUsed = sub?.current_month_library_exports ?? 0;
                        const libClipsLimit = sub?.plan?.max_library_exports ?? 0;
                        const libClipsPercent = libClipsLimit > 0 && libClipsLimit < 1000000 ? Math.min((libClipsUsed / libClipsLimit) * 100, 100) : 0;

                        return (
                        <TableRow key={p.id}>
                          <TableCell className="font-medium truncate">{p.user_name || "—"}</TableCell>
                          <TableCell className="truncate">{p.user_email || "—"}</TableCell>
                          <TableCell className="whitespace-nowrap">{parseInt(p.total_views || "0", 10).toLocaleString()}</TableCell>
                          <TableCell>{p.clip_count || 0}</TableCell>
                          <TableCell>
                            {p.disqualified ? (
                              <Badge variant="destructive" title={p.disqualification_reason || undefined}>
                                {t("admin.challenges.disqualified")}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-green-600 border-green-600">
                                {t("admin.challenges.qualified")}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {sub && (
                              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                                <div>
                                  <div className="flex items-center justify-between text-sm mb-1 whitespace-nowrap">
                                    <span className="flex items-center gap-1.5 text-muted-foreground">
                                      <Clock className="w-3.5 h-3.5 shrink-0" />
                                      {t('sidebar.time')}
                                    </span>
                                    <span className="tabular-nums text-sm">
                                      {formatHoursMinutes(timeUsed)} / {timeLimit > 0 ? formatHoursMinutes(timeLimit) : '-'}
                                      {timeLimit > 0 && <span className="ml-1 font-medium">{Math.round(timePercent)}%</span>}
                                    </span>
                                  </div>
                                  <Progress value={timePercent} className="h-1.5" />
                                </div>
                                <div>
                                  <div className="flex items-center justify-between text-sm mb-1 whitespace-nowrap">
                                    <span className="flex items-center gap-1.5 text-muted-foreground">
                                      <Scissors className="w-3.5 h-3.5 shrink-0" />
                                      {t('sidebar.clips')}
                                    </span>
                                    <span className="tabular-nums text-sm">
                                      {clipsUsed} / {clipsLimit >= 1000000 ? '∞' : clipsLimit}
                                      {clipsLimit > 0 && clipsLimit < 1000000 && <span className="ml-1 font-medium">{Math.round(clipsPercent)}%</span>}
                                    </span>
                                  </div>
                                  <Progress value={clipsPercent} className="h-1.5" />
                                </div>
                                <div>
                                  <div className="flex items-center justify-between text-sm mb-1 whitespace-nowrap">
                                    <span className="flex items-center gap-1.5 text-muted-foreground">
                                      <BookOpen className="w-3.5 h-3.5 shrink-0" />
                                      {t('admin.users.library')}
                                    </span>
                                    <span className="tabular-nums text-sm">
                                      {formatHoursMinutes(libTimeUsed)} / {libTimeLimit > 0 ? formatHoursMinutes(libTimeLimit) : '-'}
                                      {libTimeLimit > 0 && <span className="ml-1 font-medium">{Math.round(libTimePercent)}%</span>}
                                    </span>
                                  </div>
                                  <Progress value={libTimePercent} className="h-1.5" />
                                </div>
                                <div>
                                  <div className="flex items-center justify-between text-sm mb-1 whitespace-nowrap">
                                    <span className="flex items-center gap-1.5 text-muted-foreground">
                                      <Film className="w-3.5 h-3.5 shrink-0" />
                                      {t('sidebar.library')} {t('sidebar.clips').toLowerCase()}
                                    </span>
                                    <span className="tabular-nums text-sm">
                                      {libClipsUsed} / {libClipsLimit >= 1000000 ? '∞' : libClipsLimit}
                                      {libClipsLimit > 0 && libClipsLimit < 1000000 && <span className="ml-1 font-medium">{Math.round(libClipsPercent)}%</span>}
                                    </span>
                                  </div>
                                  <Progress value={libClipsPercent} className="h-1.5" />
                                </div>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">{formatDateTime(p.createdAt)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/admin/challenges/${challengeId}/participants/${p.id}/audit`)}
                                title={t("admin.challenges.auditParticipant")}
                              >
                                <ShieldAlert className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className={p.disqualified ? "text-green-600 hover:text-green-700" : "text-orange-500 hover:text-orange-600"}
                                onClick={() => {
                                  setSelectedParticipant(p);
                                  setDisqualificationReason(p.disqualification_reason || "");
                                  setIsDisqualifyModalOpen(true);
                                }}
                                title={p.disqualified ? t("admin.challenges.requalify") : t("admin.challenges.disqualify")}
                              >
                                {p.disqualified ? <RotateCcw className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-orange-500 hover:text-orange-600"
                                onClick={() => { setSelectedParticipant(p); setIsResetUsageModalOpen(true); }}
                                title={t("admin.challenges.resetUsage")}
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-600"
                                onClick={() => { setSelectedParticipant(p); setIsRemoveModalOpen(true); }}
                                title={t("admin.challenges.removeParticipant")}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                      {participants.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                            {t("admin.challenges.noParticipants")}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Add Participant Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("admin.challenges.addParticipant")}</DialogTitle>
            <DialogDescription>{t("admin.challenges.addParticipantDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="participant-email">{t("admin.challenges.participantEmail")}</Label>
            <Input
              id="participant-email"
              type="email"
              value={participantEmail}
              onChange={(e) => setParticipantEmail(e.target.value)}
              placeholder="user@example.com"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddModalOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleAddParticipant} disabled={addingParticipant || !participantEmail}>
              {addingParticipant ? t("admin.challenges.adding") : t("admin.challenges.addParticipant")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disqualify Modal */}
      <Dialog open={isDisqualifyModalOpen} onOpenChange={(open) => {
        setIsDisqualifyModalOpen(open);
        if (!open) { setSelectedParticipant(null); setDisqualificationReason(""); }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedParticipant?.disqualified
                ? t("admin.challenges.requalify")
                : t("admin.challenges.disqualify")}
            </DialogTitle>
            <DialogDescription>
              {selectedParticipant?.disqualified
                ? t("admin.challenges.requalifyConfirm", { name: selectedParticipant?.user_name || selectedParticipant?.user_email })
                : t("admin.challenges.disqualifyConfirm", { name: selectedParticipant?.user_name || selectedParticipant?.user_email })}
            </DialogDescription>
          </DialogHeader>
          {!selectedParticipant?.disqualified && (
            <div className="space-y-2">
              <Label htmlFor="disqualification-reason">{t("admin.challenges.disqualificationReason")}</Label>
              <Textarea
                id="disqualification-reason"
                value={disqualificationReason}
                onChange={(e) => setDisqualificationReason(e.target.value)}
                placeholder={t("admin.challenges.disqualificationReasonPlaceholder")}
                rows={3}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsDisqualifyModalOpen(false); setSelectedParticipant(null); setDisqualificationReason(""); }}>
              {t("common.cancel")}
            </Button>
            <Button
              variant={selectedParticipant?.disqualified ? "default" : "destructive"}
              onClick={handleDisqualify}
              disabled={disqualifying || (!selectedParticipant?.disqualified && !disqualificationReason)}
            >
              {disqualifying
                ? t("admin.challenges.disqualifying")
                : selectedParticipant?.disqualified
                  ? t("admin.challenges.requalify")
                  : t("admin.challenges.disqualify")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Confirmation */}
      <AlertDialog open={isRemoveModalOpen} onOpenChange={setIsRemoveModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.challenges.removeParticipant")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.challenges.removeParticipantConfirm", { name: selectedParticipant?.user_name || selectedParticipant?.user_email })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveParticipant} disabled={removingParticipant} className="bg-red-500 hover:bg-red-600">
              {removingParticipant ? t("admin.challenges.removing") : t("common.remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Usage Confirmation */}
      <AlertDialog open={isResetUsageModalOpen} onOpenChange={setIsResetUsageModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.challenges.resetUsage")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.challenges.resetUsageConfirm", { name: selectedParticipant?.user_name || selectedParticipant?.user_email })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetUsage} disabled={resettingUsage} className="bg-orange-500 hover:bg-orange-600">
              {resettingUsage ? t("admin.challenges.resettingUsage") : t("admin.challenges.resetUsage")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset All Usage Confirmation */}
      <AlertDialog open={isResetAllUsageModalOpen} onOpenChange={setIsResetAllUsageModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.challenges.resetAllUsage")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.challenges.resetAllUsageConfirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetAllUsage} disabled={resettingAllUsage} className="bg-orange-500 hover:bg-orange-600">
              {resettingAllUsage ? t("admin.challenges.resettingUsage") : t("admin.challenges.resetAllUsage")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Participants Modal */}
      <Dialog open={isImportModalOpen} onOpenChange={(open) => {
        if (!isImporting) {
          setIsImportModalOpen(open);
          if (!open) setImportResults(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("admin.challenges.importParticipants")}</DialogTitle>
            <DialogDescription>{t("admin.challenges.importParticipantsDesc")}</DialogDescription>
          </DialogHeader>

          {!importResults ? (
            <div className="space-y-4">
              <Label htmlFor="import-file">{t("admin.challenges.selectFile")}</Label>
              <Input
                id="import-file"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleImportFile}
                disabled={isImporting}
              />
              {isImporting && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                  {t("admin.challenges.importing")}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm font-medium">{t("admin.challenges.importSummary")}</p>
              <div className="space-y-2 text-sm">
                {importResults.added.length > 0 && (
                  <div>
                    <p className="font-medium text-green-600">
                      {t("admin.challenges.importAdded")} ({importResults.added.length})
                    </p>
                    <ul className="ml-4 list-disc text-muted-foreground max-h-24 overflow-y-auto">
                      {importResults.added.map((e) => <li key={e}>{e}</li>)}
                    </ul>
                  </div>
                )}
                {importResults.created.length > 0 && (
                  <div>
                    <p className="font-medium text-blue-600">
                      {t("admin.challenges.importCreated")} ({importResults.created.length})
                    </p>
                    <ul className="ml-4 list-disc text-muted-foreground max-h-24 overflow-y-auto">
                      {importResults.created.map((e) => <li key={e}>{e}</li>)}
                    </ul>
                  </div>
                )}
                {importResults.skipped.length > 0 && (
                  <div>
                    <p className="font-medium text-yellow-600">
                      {t("admin.challenges.importSkipped")} ({importResults.skipped.length})
                    </p>
                    <ul className="ml-4 list-disc text-muted-foreground max-h-24 overflow-y-auto">
                      {importResults.skipped.map((e) => <li key={e}>{e}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            {importResults ? (
              <Button onClick={() => { setIsImportModalOpen(false); setImportResults(null); }}>
                {t("admin.challenges.importDone")}
              </Button>
            ) : (
              <Button variant="outline" onClick={() => setIsImportModalOpen(false)} disabled={isImporting}>
                {t("common.cancel")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminChallengeParticipants;
